import { useCheckout } from '@/hooks/useCheckout'
import { useCreatePaymentIntent } from '@/hooks/useCreatePaymentIntent'
import { type PaymentMethod, useExtraCheckoutMethods } from '@/hooks/useExtraCheckoutMethods'
import { PlanDetail } from '@/utils/data/plans'
import { useEffect, useRef, useState } from 'react'
import { Loader } from './Loader'

type CheckoutMethodProp = {
  paymentMethod: PaymentMethod
  selectedPlan: PlanDetail
  coupons?: string[]
  setLoading: (isLoading: boolean) => void
}

const CheckoutMethod = ({ paymentMethod, selectedPlan, coupons, setLoading }: CheckoutMethodProp) => {
  const { createPaymentIntentAsync } = useCreatePaymentIntent()
  const { checkoutSubscription } = useCheckout()
  const [paymentIntentId, setPaymentIntentId] = useState<string | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const [cbHandler, setCbHandler] = useState<ChargebeeHandler | null>(null)
  const paymentButtonRef = useRef<HTMLDivElement>(null)

  const loadPayment = async () => {
    try {
      setIsLoading(true)
      const cbInstance = window.Chargebee?.getInstance()
      const handler = await cbInstance.load(paymentMethod.handlerKey)
      const intent = await createPaymentIntentAsync({
        paymentMethodType: paymentMethod.type,
        intentPurpose: 'create',
        plans: [selectedPlan.plan_id],
        coupons,
      })
      handler.setPaymentIntent(intent)
      setIsLoading(false)
      setCbHandler(handler)
    } catch {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (cbHandler) {
      cbHandler.mountPaymentButton(`#${paymentMethod.buttonId}`).then(() => {
        cbHandler
          .handlePayment()
          .then((authorizedPaymentIntentResponse) => {
            if (
              authorizedPaymentIntentResponse &&
              typeof authorizedPaymentIntentResponse === 'object' &&
              'paymentIntent' in authorizedPaymentIntentResponse
            ) {
              setPaymentIntentId(authorizedPaymentIntentResponse.paymentIntent.id)
            } else {
              setPaymentIntentId(authorizedPaymentIntentResponse.id)
            }
          })
          .catch((error) => {
            if ((error as { statusCode: string }).statusCode === 'CANCELED') {
              window.location.reload()
            }
          })
      })
    }
  }, [cbHandler, coupons])

  useEffect(() => {
    loadPayment()
  }, [selectedPlan, coupons])

  useEffect(() => {
    if (paymentIntentId) {
      checkoutSubscription({
        plans: selectedPlan ? [selectedPlan.plan_id] : [],
        coupons,
        paymentIntentId,
      })
    }
  }, [paymentIntentId])

  useEffect(() => {
    setLoading(isLoading)
  }, [isLoading])
  return (
    <>
      {!isLoading && (
        <div
          ref={paymentButtonRef}
          style={{
            width: '10rem',
            height: '40px',
            maxHeight: '40px',
            maxWidth: '10rem',
          }}
          id={paymentMethod.buttonId}
        />
      )}
    </>
  )
}

const CheckoutMethods = ({
  selectedPlan,
  coupons,
}: {
  selectedPlan: PlanDetail
  coupons?: string[]
}) => {
  const { paymentMethods } = useExtraCheckoutMethods()
  const { isSubscribing } = useCheckout()
  const [isLoading, setIsLoading] = useState(true)
  return (
    <>
      {isSubscribing === false && (
        <div className="flex gap-4 items-center justify-evenly">
          {paymentMethods.map((method) => (
            <CheckoutMethod
              key={method.handlerKey}
              paymentMethod={method}
              selectedPlan={selectedPlan}
              coupons={coupons}
              setLoading={setIsLoading}
            />
          ))}
        </div>
      )}
      {(isSubscribing || isLoading) && (
        <div className="flex w-full items-center justify-center">
          <Loader />
        </div>
      )}
    </>
  )
}

export default CheckoutMethods
